import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, AlertTriangle, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Conversion constants
const ACRES_PER_HECTARE = 2.471;
const GUNTAS_PER_ACRE = 40;
const CENTS_PER_ACRE = 100;

const Step3Agricultural = ({ formData, onChange, errors = {} }) => {
  // Master data
  const [cropCategories, setCropCategories] = useState([]);
  const [cropYieldUnits, setCropYieldUnits] = useState({});
  const [seasons, setSeasons] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);

  // Location master data for dropdowns
  const [states, setStates] = useState([]);
  const [districtsByParcel, setDistrictsByParcel] = useState({});
  const [taluksByParcel, setTaluksByParcel] = useState({});
  const [villagesByParcel, setVillagesByParcel] = useState({});

  // Section A: Land Parcels
  const [parcels, setParcels] = useState([{
    parcel_id: `parcel-${Date.now()}`,
    survey_number: '',
    area: 0,
    area_unit: formData.land_area_unit || '',
    area_acres: '',
    area_subunit: '',
    state_id: '',
    state: formData.residential_state || '',
    district_id: '',
    district: formData.residential_district || '',
    taluk_id: '',
    taluk: '',
    village_id: '',
    village: '',
    distance_to_market_km: '',
    ownership_status: '',
    irrigation_status: '',
    soil_type: '',
    soil_texture: ''
  }]);

  // Section B: Crops
  const [crops, setCrops] = useState([{
    crop_id: `crop-${Date.now()}`,
    linked_parcel_id: '',
    category: '',
    crop_type: '',
    year: '',
    season: '',
    area: '',
    yield_history: [],
    avg_yield: null,
    yield_variance: null,
    yield_unit: '',
    availableCrops: []
  }]);

  // Calculated values
  const [totalLandArea, setTotalLandArea] = useState(0);
  const [totalLandHectares, setTotalLandHectares] = useState(0);
  const [lfi, setLfi] = useState(null);
  const [cdi, setCdi] = useState(null);
  const [fallowLandByParcel, setFallowLandByParcel] = useState({});

  // Fetch master data on mount
  useEffect(() => {
    fetchMasterData();
    fetchStates();
  }, []);

  // Initialize parcels and crops from formData in edit mode
  useEffect(() => {
    if (formData.land_parcels && formData.land_parcels.length > 0 && formData.land_parcels[0].parcel_id) {
      setParcels(formData.land_parcels.map(p => ({
        parcel_id: p.parcel_id || `parcel-${Date.now()}`,
        survey_number: p.survey_number || '',
        area: p.area || 0,
        area_unit: p.area_unit || formData.land_area_unit || '',
        area_acres: p.area_acres || '',
        area_subunit: p.area_subunit || '',
        state_id: p.state_id || '',
        state: p.state || formData.residential_state || '',
        district_id: p.district_id || '',
        district: p.district || formData.residential_district || '',
        taluk_id: p.taluk_id || '',
        taluk: p.taluk || '',
        village_id: p.village_id || '',
        village: p.village || '',
        distance_to_market_km: p.distance_to_market_km || '',
        ownership_status: p.ownership_status || '',
        irrigation_status: p.irrigation_status || '',
        soil_type: p.soil_type || '',
        soil_texture: p.soil_texture || ''
      })));
    }
    if (formData.crops && formData.crops.length > 0 && formData.crops[0].crop_id) {
      setCrops(formData.crops.map(c => ({
        crop_id: c.crop_id || `crop-${Date.now()}`,
        linked_parcel_id: c.linked_parcel_id || '',
        category: c.category || '',
        crop_type: c.crop_type || '',
        year: c.year || '',
        season: c.season || '',
        area: c.area || '',
        yield_history: c.yield_history || [],
        avg_yield: c.avg_yield || null,
        yield_variance: c.yield_variance || null,
        yield_unit: c.yield_unit || '',
        availableCrops: []
      })));
    }
  }, []);

  // Hydrate location IDs for ALL parcels when states are loaded (edit mode)
  // Resolves state → district → taluk → village cascading dropdown data
  useEffect(() => {
    if (states.length === 0 || parcels.length === 0) return;

    const hydrateParcelLocations = async () => {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      for (const parcel of parcels) {
        const pid = parcel.parcel_id;
        const stateName = parcel.state || formData.residential_state;
        if (!stateName) continue;

        // Resolve state_id
        const stateObj = states.find(s => s.name === stateName);
        if (!stateObj) continue;

        if (!parcel.state_id) {
          setParcels(prev => prev.map(p => p.parcel_id === pid ? { ...p, state_id: stateObj.id } : p));
        }

        // Fetch districts
        try {
          const distRes = await axios.get(`${API}/master-data/districts/${stateObj.id}`, { headers });
          const districts = distRes.data.districts || distRes.data;
          setDistrictsByParcel(prev => ({ ...prev, [pid]: districts }));

          const distName = parcel.district || formData.residential_district;
          if (!distName) continue;
          const distObj = districts.find(d => d.name === distName);
          if (!distObj) continue;

          if (!parcel.district_id) {
            setParcels(prev => prev.map(p => p.parcel_id === pid ? { ...p, district_id: distObj.id } : p));
          }

          // Fetch taluks
          const talukRes = await axios.get(`${API}/master-data/taluks/${distObj.id}`, { headers });
          const taluks = talukRes.data.taluks || talukRes.data;
          setTaluksByParcel(prev => ({ ...prev, [pid]: taluks }));

          const talukName = parcel.taluk;
          if (!talukName) continue;
          const talukObj = taluks.find(t => t.name === talukName);
          if (!talukObj) continue;

          if (!parcel.taluk_id) {
            setParcels(prev => prev.map(p => p.parcel_id === pid ? { ...p, taluk_id: talukObj.id } : p));
          }

          // Fetch villages
          const villageRes = await axios.get(`${API}/master-data/villages/${talukObj.id}`, { headers });
          const villages = villageRes.data.villages || villageRes.data;
          setVillagesByParcel(prev => ({ ...prev, [pid]: villages }));

          const villageName = parcel.village;
          if (!villageName) continue;
          // Villages are now objects {name, lgd_code} or plain strings
          const isObj = villages.length > 0 && typeof villages[0] === 'object';
          let villageVal = null;
          let villageLgd = '';
          if (isObj) {
            const vObj = villages.find(v => v.name === villageName);
            villageVal = vObj?.name || null;
            villageLgd = vObj?.lgd_code || '';
          } else {
            villageVal = villages.includes(villageName) ? villageName : null;
          }

          if (villageVal && !parcel.village_id) {
            setParcels(prev => prev.map(p => p.parcel_id === pid ? { ...p, village_id: villageVal, lgd_code: villageLgd } : p));
          }
        } catch (err) {
          console.error(`Error hydrating parcel ${pid}:`, err);
        }
      }
    };

    // Only run hydration once parcels have name data (edit mode)
    const hasNameData = parcels.some(p => (p.state || p.district || p.taluk || p.village) && !p.state_id);
    if (hasNameData) {
      hydrateParcelLocations();
    }
  }, [states]);

  // Hydrate crop availableCrops for edit mode when cropCategories are loaded
  useEffect(() => {
    if (cropCategories.length === 0 || crops.length === 0) return;

    const hydrateCrops = async () => {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      for (const crop of crops) {
        if (crop.category && crop.availableCrops.length === 0) {
          try {
            const categoryName = crop.category.replace('_', ' ');
            const response = await axios.get(`${API}/master-data/crops/category/${categoryName}`, { headers });
            const availableCrops = response.data.crops || [];
            setCrops(prev => prev.map(c =>
              c.crop_id === crop.crop_id ? { ...c, availableCrops } : c
            ));
          } catch (err) {
            console.error(`Error fetching crops for category ${crop.category}:`, err);
          }
        }
      }
    };

    const needsHydration = crops.some(c => c.category && c.availableCrops.length === 0);
    if (needsHydration) {
      hydrateCrops();
    }
  }, [cropCategories]);

  // Auto-fill state/district in parcels when residential location changes
  useEffect(() => {
    if (formData.residential_state || formData.residential_district) {
      setParcels(prev => prev.map(p => ({
        ...p,
        state: formData.residential_state || p.state,
        district: formData.residential_district || p.district
      })));
    }
  }, [formData.residential_state, formData.residential_district]);

  // Update area_unit in all parcels when land_area_unit changes
  useEffect(() => {
    if (formData.land_area_unit) {
      setParcels(prev => prev.map(p => ({
        ...p,
        area_unit: formData.land_area_unit
      })));
    }
  }, [formData.land_area_unit]);

  // Calculate totals when parcels change
  useEffect(() => {
    calculateLandTotalsAndLFI();
  }, [parcels, formData.land_area_unit]);

  // Calculate CDI when crops change
  useEffect(() => {
    calculateCDI();
  }, [crops]);

  // Calculate fallow land when parcels or crops change
  useEffect(() => {
    calculateFallowLand();
  }, [parcels, crops, formData.land_area_unit]);

  // Sync with formData
  useEffect(() => {
    onChange('land_parcels', parcels);
    onChange('crops', crops);
    onChange('total_land_holding_size_acres', totalLandArea);
    onChange('land_area_hectares', totalLandHectares);
    onChange('land_fragmentation_index', lfi);
    onChange('crop_diversification_index', cdi);
  }, [parcels, crops, totalLandArea, totalLandHectares, lfi, cdi]);

  const fetchMasterData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [cropsRes, yieldUnitsRes, seasonsRes, yearsRes] = await Promise.all([
        axios.get(`${API}/master-data/crops`, { headers }),
        axios.get(`${API}/master-data/crop-yield-units`, { headers }),
        axios.get(`${API}/master-data/seasons`, { headers }),
        axios.get(`${API}/master-data/years`, { headers })
      ]);

      setCropCategories(cropsRes.data.categories || []);
      
      // Create crop name to yield unit mapping
      const unitsMap = {};
      (yieldUnitsRes.data.crop_yield_mapping || []).forEach(item => {
        unitsMap[item.crop_name] = item.default_yield_unit;
      });
      setCropYieldUnits(unitsMap);
      
      setSeasons(seasonsRes.data.seasons || []);
      setYears(yearsRes.data.years || []);
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  // Location dropdown functions
  const fetchStates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/states`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStates(response.data.states || response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
      toast.error('Failed to load states');
    }
  };

  const fetchDistricts = async (parcel_id, stateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/districts/${stateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDistrictsByParcel(prev => ({ ...prev, [parcel_id]: response.data.districts || response.data }));
    } catch (error) {
      console.error('Error fetching districts:', error);
      toast.error('Failed to load districts');
    }
  };

  const fetchTaluks = async (parcel_id, districtId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/taluks/${districtId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaluksByParcel(prev => ({ ...prev, [parcel_id]: response.data.taluks || response.data }));
    } catch (error) {
      console.error('Error fetching taluks:', error);
      toast.error('Failed to load taluks');
    }
  };

  const fetchVillages = async (parcel_id, talukId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/villages/${talukId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVillagesByParcel(prev => ({ ...prev, [parcel_id]: response.data.villages || response.data }));
    } catch (error) {
      console.error('Error fetching villages:', error);
      toast.error('Failed to load villages');
    }
  };

  // ============== SECTION A: LAND PARCELS ==============
  
  const addParcel = () => {
    const newParcel = {
      parcel_id: `parcel-${Date.now()}`,
      survey_number: '',
      area: 0,
      area_unit: formData.land_area_unit || '',
      area_acres: '',
      area_subunit: '',
      state_id: '',
      state: formData.residential_state || '',
      district_id: '',
      district: formData.residential_district || '',
      taluk_id: '',
      taluk: '',
      village_id: '',
      village: '',
      distance_to_market_km: '',
      ownership_status: '',
      irrigation_status: '',
      soil_type: '',
      soil_texture: ''
    };
    setParcels(prev => [...prev, newParcel]);
    
    // Auto-fetch districts if residential state is set
    if (formData.residential_state) {
      const state = states.find(s => s.name === formData.residential_state);
      if (state) {
        fetchDistricts(newParcel.parcel_id, state.id);
        // Also set the state_id
        setTimeout(() => {
          setParcels(prev => prev.map(p => 
            p.parcel_id === newParcel.parcel_id ? { ...p, state_id: state.id } : p
          ));
        }, 100);
      }
    }
  };

  const removeParcel = (parcel_id) => {
    if (parcels.length === 1) {
      toast.error('At least one parcel is required');
      return;
    }
    
    // Check if any crop is linked to this parcel
    const linkedCrops = crops.filter(c => c.linked_parcel_id === parcel_id);
    if (linkedCrops.length > 0) {
      toast.error(`Cannot remove parcel. ${linkedCrops.length} crop(s) are linked to it.`);
      return;
    }
    
    setParcels(prev => prev.filter(p => p.parcel_id !== parcel_id));
  };

  const updateParcel = (parcel_id, field, value) => {
    setParcels(prev => prev.map(p => {
      if (p.parcel_id === parcel_id) {
        const updated = { ...p, [field]: value };
        
        // If updating area-related fields, also update the main area field
        const unit = formData.land_area_unit;
        if (field === 'area' && unit === 'Hectares') {
          updated.area = parseFloat(value) || 0;
        } else if ((field === 'area_acres' || field === 'area_subunit') && (unit === 'Acres & Guntas' || unit === 'Acres & Cents')) {
          updated.area = parseFloat(updated.area_acres) || 0;
        }
        
        return updated;
      }
      return p;
    }));
  };

  // Location handlers for parcels
  const handleParcelStateChange = (parcel_id, stateId) => {
    const selectedState = states.find(s => s.id === stateId);
    setParcels(prev => prev.map(p => 
      p.parcel_id === parcel_id ? {
        ...p,
        state_id: stateId,
        state: selectedState?.name || '',
        district_id: '',
        district: '',
        taluk_id: '',
        taluk: '',
        village_id: '',
        village: ''
      } : p
    ));
    
    if (stateId) {
      fetchDistricts(parcel_id, stateId);
    }
  };

  const handleParcelDistrictChange = (parcel_id, districtId) => {
    const districts = districtsByParcel[parcel_id] || [];
    const selectedDistrict = districts.find(d => d.id === districtId);
    setParcels(prev => prev.map(p => 
      p.parcel_id === parcel_id ? {
        ...p,
        district_id: districtId,
        district: selectedDistrict?.name || '',
        taluk_id: '',
        taluk: '',
        village_id: '',
        village: ''
      } : p
    ));
    
    if (districtId) {
      fetchTaluks(parcel_id, districtId);
    }
  };

  const handleParcelTalukChange = (parcel_id, talukId) => {
    const taluks = taluksByParcel[parcel_id] || [];
    const selectedTaluk = taluks.find(t => t.id === talukId);
    setParcels(prev => prev.map(p => 
      p.parcel_id === parcel_id ? {
        ...p,
        taluk_id: talukId,
        taluk: selectedTaluk?.name || '',
        village_id: '',
        village: ''
      } : p
    ));
    
    if (talukId) {
      fetchVillages(parcel_id, talukId);
    }
  };

  const handleParcelVillageChange = (parcel_id, villageId) => {
    const villages = villagesByParcel[parcel_id] || [];
    // Villages can be plain strings or objects {name, lgd_code}
    const isObj = villages.length > 0 && typeof villages[0] === 'object';
    const selectedVillage = isObj
      ? villages.find(v => (v.name || v.id) === villageId)
      : null;
    setParcels(prev => prev.map(p => 
      p.parcel_id === parcel_id ? {
        ...p,
        village_id: villageId,
        village: isObj ? (selectedVillage?.name || '') : villageId,
        lgd_code: selectedVillage?.lgd_code || ''
      } : p
    ));
  };

  const convertToAcres = (area, acres, subunit, unit) => {
    if (unit === 'Hectares') {
      return area * ACRES_PER_HECTARE;
    } else if (unit === 'Acres & Guntas') {
      return parseFloat(acres || 0) + parseFloat(subunit || 0) / GUNTAS_PER_ACRE;
    } else if (unit === 'Acres & Cents') {
      return parseFloat(acres || 0) + parseFloat(subunit || 0) / CENTS_PER_ACRE;
    }
    return 0;
  };

  const calculateLandTotalsAndLFI = () => {
    const unit = formData.land_area_unit;
    let totalAcres = 0;

    // Update each parcel's area field and calculate total
    const updatedParcels = parcels.map(p => {
      let calculatedArea = 0;
      
      if (unit === 'Hectares') {
        calculatedArea = parseFloat(p.area) || 0;
      } else if (unit === 'Acres & Guntas') {
        calculatedArea = parseFloat(p.area_acres) || 0;
      } else if (unit === 'Acres & Cents') {
        calculatedArea = parseFloat(p.area_acres) || 0;
      }
      
      const acresValue = convertToAcres(
        parseFloat(p.area) || 0,
        parseFloat(p.area_acres) || 0,
        parseFloat(p.area_subunit) || 0,
        unit
      );
      totalAcres += acresValue;
      
      // Update the parcel with calculated area if it changed
      if (calculatedArea !== p.area) {
        return { ...p, area: calculatedArea };
      }
      return p;
    });
    
    // Update parcels state if any changed
    if (JSON.stringify(updatedParcels) !== JSON.stringify(parcels)) {
      setParcels(updatedParcels);
    }

    setTotalLandArea(totalAcres);
    setTotalLandHectares(totalAcres / ACRES_PER_HECTARE);
    
    // LFI = Number of parcels / Total land area
    if (totalAcres > 0) {
      setLfi(parcels.length / totalAcres);
    } else {
      setLfi(null);
    }
  };

  // ============== SECTION B: CROPS ==============

  const addCrop = () => {
    if (crops.length >= 5) {
      toast.error('Maximum 5 crops allowed');
      return;
    }
    
    const newCrop = {
      crop_id: `crop-${Date.now()}`,
      linked_parcel_id: '',
      category: '',
      crop_type: '',
      year: '',
      season: '',
      area: '',
      yield_history: [],
      avg_yield: null,
      yield_variance: null,
      yield_unit: '',
      availableCrops: []
    };
    setCrops(prev => [...prev, newCrop]);
  };

  const removeCrop = (crop_id) => {
    if (crops.length === 1) {
      toast.error('At least one crop is required');
      return;
    }
    setCrops(prev => prev.filter(c => c.crop_id !== crop_id));
  };

  const updateCrop = (crop_id, field, value) => {
    setCrops(prev => prev.map(c => 
      c.crop_id === crop_id ? { ...c, [field]: value } : c
    ));
  };

  const fetchCropsForCategory = async (crop_id, category) => {
    try {
      const token = localStorage.getItem('token');
      const categoryName = category.replace('_', ' ');
      const response = await axios.get(`${API}/master-data/crops/category/${categoryName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCrops(prev => prev.map(c => 
        c.crop_id === crop_id ? { ...c, availableCrops: response.data.crops || [] } : c
      ));
    } catch (error) {
      console.error('Error fetching crops:', error);
      toast.error('Failed to load crops for category');
    }
  };

  const handleCropCategoryChange = (crop_id, category) => {
    updateCrop(crop_id, 'category', category);
    updateCrop(crop_id, 'crop_type', '');
    updateCrop(crop_id, 'availableCrops', []);
    
    if (category) {
      fetchCropsForCategory(crop_id, category);
    }
  };

  const handleCropTypeChange = (crop_id, cropType) => {
    updateCrop(crop_id, 'crop_type', cropType);
    
    // Auto-set yield unit based on crop type
    const yieldUnit = cropYieldUnits[cropType] || 'quintals/acre';
    updateCrop(crop_id, 'yield_unit', yieldUnit);
  };

  const calculateCDI = () => {
    // CDI based on crop_type + season combinations
    const validCrops = crops.filter(c => c.crop_type && c.season && c.area);
    
    if (validCrops.length === 0) {
      setCdi(null);
      return;
    }

    const totalArea = validCrops.reduce((sum, c) => sum + (parseFloat(c.area) || 0), 0);
    
    if (totalArea === 0) {
      setCdi(null);
      return;
    }

    // Calculate HHI based on crop_type + season combinations
    const shares = validCrops.map(c => (parseFloat(c.area) || 0) / totalArea);
    const hhi = shares.reduce((sum, share) => sum + (share * share), 0);
    const cdiValue = 1 - hhi;
    
    setCdi(cdiValue);
  };

  const calculateFallowLand = () => {
    const unit = formData.land_area_unit;
    const fallowMap = {};

    parcels.forEach(parcel => {
      const parcelAcres = convertToAcres(
        parseFloat(parcel.area) || 0,
        parseFloat(parcel.area_acres) || 0,
        parseFloat(parcel.area_subunit) || 0,
        unit
      );

      // Sum crop areas for this parcel
      const cropsOnParcel = crops.filter(c => c.linked_parcel_id === parcel.parcel_id);
      const cropAreaSum = cropsOnParcel.reduce((sum, c) => sum + (parseFloat(c.area) || 0), 0);

      fallowMap[parcel.parcel_id] = Math.max(0, parcelAcres - cropAreaSum);
    });

    setFallowLandByParcel(fallowMap);
  };

  // ============== SECTION C: YIELD HISTORY ==============

  const generateYieldHistory = (crop) => {
    if (!crop.year || !crop.season) return [];
    
    const cropYear = parseInt(crop.year);
    const history = [];
    
    for (let i = 2; i >= 0; i--) {
      history.push({
        year: cropYear - i,
        season: crop.season,
        yield_value: 0,
        yield_unit: crop.yield_unit || 'quintals/acre'
      });
    }
    
    return history;
  };

  const updateYieldHistory = (crop_id, yearIndex, value) => {
    setCrops(prev => prev.map(c => {
      if (c.crop_id === crop_id) {
        const updatedHistory = [...(c.yield_history || [])];
        if (updatedHistory[yearIndex]) {
          updatedHistory[yearIndex].yield_value = parseFloat(value) || 0;
        }
        
        // Calculate avg_yield and yield_variance
        const yieldValues = updatedHistory.map(y => y.yield_value).filter(v => v > 0);
        
        if (yieldValues.length === 3) {
          const avgYield = yieldValues.reduce((sum, v) => sum + v, 0) / 3;
          const variance = Math.sqrt(
            yieldValues.reduce((sum, v) => sum + Math.pow(v - avgYield, 2), 0) / 2
          );
          
          return {
            ...c,
            yield_history: updatedHistory,
            avg_yield: avgYield,
            yield_variance: variance
          };
        }
        
        return { ...c, yield_history: updatedHistory };
      }
      return c;
    }));
  };

  // Auto-generate yield history when year/season is selected
  useEffect(() => {
    setCrops(prev => prev.map(c => {
      if (c.year && c.season && c.yield_unit && (!c.yield_history || c.yield_history.length === 0)) {
        return { ...c, yield_history: generateYieldHistory(c) };
      }
      return c;
    }));
  }, [crops.map(c => `${c.crop_id}-${c.year}-${c.season}-${c.yield_unit}`).join('|')]);

  // Validation helper
  const validateParcelAreas = () => {
    const warnings = [];
    const unit = formData.land_area_unit;

    parcels.forEach((parcel, index) => {
      const parcelAcres = convertToAcres(
        parseFloat(parcel.area) || 0,
        parseFloat(parcel.area_acres) || 0,
        parseFloat(parcel.area_subunit) || 0,
        unit
      );

      const cropsOnParcel = crops.filter(c => c.linked_parcel_id === parcel.parcel_id);
      const cropAreaSum = cropsOnParcel.reduce((sum, c) => sum + (parseFloat(c.area) || 0), 0);

      if (cropAreaSum > parcelAcres) {
        warnings.push(`Parcel ${index + 1}: Crop areas (${cropAreaSum.toFixed(2)} acres) exceed parcel area (${parcelAcres.toFixed(2)} acres)`);
      }
    });

    return warnings;
  };

  return (
    <div className="space-y-8">
      {/* ============== LAND MEASUREMENT UNIT ============== */}
      <Card>
        <CardHeader>
          <CardTitle>Land Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Land Measurement Unit <span className="text-red-500">*</span></Label>
            <Select
              value={formData.land_area_unit || ''}
              onValueChange={(value) => onChange('land_area_unit', value)}
            >
              <SelectTrigger className={errors.land_area_unit ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select measurement unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Hectares">Hectares</SelectItem>
                <SelectItem value="Acres & Guntas">Acres & Guntas</SelectItem>
                <SelectItem value="Acres & Cents">Acres & Cents</SelectItem>
              </SelectContent>
            </Select>
            {errors.land_area_unit && <p className="text-sm text-red-500">{errors.land_area_unit}</p>}
            <p className="text-sm text-gray-500">
              This unit will be used for all land and crop area measurements
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ============== SECTION A: LAND PARCELS ============== */}
      <Card>
        <CardHeader>
          <CardTitle>Land Parcels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">{!formData.land_area_unit && (
            <div className="text-center text-gray-500 py-4">
              Please select Land Measurement Unit first
            </div>
          )}
          
          {formData.land_area_unit && parcels.map((parcel, index) => (
            <div key={parcel.parcel_id} className="border rounded-lg p-4 space-y-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Parcel {index + 1}</h4>
                {parcels.length > 1 && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeParcel(parcel.parcel_id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Survey Number */}
                <div className="space-y-2">
                  <Label>Survey Number (Optional)</Label>
                  <Input
                    value={parcel.survey_number}
                    onChange={(e) => updateParcel(parcel.parcel_id, 'survey_number', e.target.value)}
                    placeholder="Enter survey number"
                  />
                </div>

                {/* Area Input - Dynamic based on unit */}
                {formData.land_area_unit === 'Hectares' && (
                  <div className="space-y-2">
                    <Label>Area (Hectares) <span className="text-red-500">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={parcel.area}
                      onChange={(e) => updateParcel(parcel.parcel_id, 'area', e.target.value)}
                      placeholder="Enter hectares"
                    />
                  </div>
                )}

                {formData.land_area_unit === 'Acres & Guntas' && (
                  <>
                    <div className="space-y-2">
                      <Label>Acres <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        min="0"
                        value={parcel.area_acres}
                        onChange={(e) => updateParcel(parcel.parcel_id, 'area_acres', e.target.value)}
                        placeholder="Enter acres"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Guntas</Label>
                      <Input
                        type="number"
                        min="0"
                        max="39"
                        value={parcel.area_subunit}
                        onChange={(e) => updateParcel(parcel.parcel_id, 'area_subunit', e.target.value)}
                        placeholder="Enter guntas"
                      />
                    </div>
                  </>
                )}

                {formData.land_area_unit === 'Acres & Cents' && (
                  <>
                    <div className="space-y-2">
                      <Label>Acres <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        min="0"
                        value={parcel.area_acres}
                        onChange={(e) => updateParcel(parcel.parcel_id, 'area_acres', e.target.value)}
                        placeholder="Enter acres"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cents</Label>
                      <Input
                        type="number"
                        min="0"
                        max="99"
                        value={parcel.area_subunit}
                        onChange={(e) => updateParcel(parcel.parcel_id, 'area_subunit', e.target.value)}
                        placeholder="Enter cents"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Location Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* State Dropdown */}
                <div className="space-y-2">
                  <Label>State <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.state_id || ''}
                    onValueChange={(value) => handleParcelStateChange(parcel.parcel_id, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state.id} value={state.id}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Auto-filled from residential, can be changed</p>
                </div>

                {/* District Dropdown */}
                <div className="space-y-2">
                  <Label>District <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.district_id || ''}
                    onValueChange={(value) => handleParcelDistrictChange(parcel.parcel_id, value)}
                    disabled={!parcel.state_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={parcel.state_id ? "Select district" : "Select state first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(districtsByParcel[parcel.parcel_id] || []).map((district) => (
                        <SelectItem key={district.id} value={district.id}>
                          {district.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Auto-filled from residential, can be changed</p>
                </div>

                {/* Taluk Dropdown */}
                <div className="space-y-2">
                  <Label>Taluk <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.taluk_id || ''}
                    onValueChange={(value) => handleParcelTalukChange(parcel.parcel_id, value)}
                    disabled={!parcel.district_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={parcel.district_id ? "Select taluk" : "Select district first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(taluksByParcel[parcel.parcel_id] || []).map((taluk) => (
                        <SelectItem key={taluk.id} value={taluk.id}>
                          {taluk.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Village Dropdown */}
                <div className="space-y-2">
                  <Label>Village <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.village_id || ''}
                    onValueChange={(value) => handleParcelVillageChange(parcel.parcel_id, value)}
                    disabled={!parcel.taluk_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={parcel.taluk_id ? "Select village" : "Select taluk first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(villagesByParcel[parcel.parcel_id] || []).map((village) => {
                        const vName = typeof village === 'string' ? village : village.name;
                        return (
                          <SelectItem key={vName} value={vName}>
                            {vName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Distance to Market */}
                <div className="space-y-2">
                  <Label>Distance to Market (km) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={parcel.distance_to_market_km}
                    onChange={(e) => updateParcel(parcel.parcel_id, 'distance_to_market_km', e.target.value)}
                    placeholder="Enter distance"
                  />
                </div>
              </div>

              {/* Parcel Characteristics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ownership Status <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.ownership_status}
                    onValueChange={(value) => updateParcel(parcel.parcel_id, 'ownership_status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select ownership" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Owned">Owned</SelectItem>
                      <SelectItem value="Leased/Sharecropped">Leased/Sharecropped</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Irrigation Status <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.irrigation_status}
                    onValueChange={(value) => updateParcel(parcel.parcel_id, 'irrigation_status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select irrigation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Irrigated">Irrigated</SelectItem>
                      <SelectItem value="Rainfed">Rainfed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Soil Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.soil_type}
                    onValueChange={(value) => updateParcel(parcel.parcel_id, 'soil_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select soil type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Alluvial Soil">Alluvial Soil</SelectItem>
                      <SelectItem value="Black Soil (Regur Soil)">Black Soil (Regur Soil)</SelectItem>
                      <SelectItem value="Red Soil">Red Soil</SelectItem>
                      <SelectItem value="Laterite Soil">Laterite Soil</SelectItem>
                      <SelectItem value="Arid Soil (Desert Soil)">Arid Soil (Desert Soil)</SelectItem>
                      <SelectItem value="Forest Soil (Mountain Soil)">Forest Soil (Mountain Soil)</SelectItem>
                      <SelectItem value="Saline Soil">Saline Soil</SelectItem>
                      <SelectItem value="Peaty Soil (Organic Soil)">Peaty Soil (Organic Soil)</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Soil Texture <span className="text-red-500">*</span></Label>
                  <Select
                    value={parcel.soil_texture}
                    onValueChange={(value) => updateParcel(parcel.parcel_id, 'soil_texture', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select soil texture" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sandy">Sandy</SelectItem>
                      <SelectItem value="Loamy">Loamy</SelectItem>
                      <SelectItem value="Clayey">Clayey</SelectItem>
                      <SelectItem value="Sandy Loam">Sandy Loam</SelectItem>
                      <SelectItem value="Clay Loam">Clay Loam</SelectItem>
                      <SelectItem value="Silty Loam">Silty Loam</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Fallow Land Info */}
              {fallowLandByParcel[parcel.parcel_id] > 0 && (
                <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                  <Info className="w-4 h-4" />
                  <span>Fallow Land: {fallowLandByParcel[parcel.parcel_id].toFixed(2)} acres</span>
                </div>
              )}
            </div>
          ))}

          <Button type="button" onClick={addParcel} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Parcel
          </Button>

          {/* Land Summary */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Total Land Area:</span>
              <span>{totalLandArea.toFixed(2)} acres ({totalLandHectares.toFixed(2)} hectares)</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Land Fragmentation Index:</span>
              <span>{lfi !== null ? lfi.toFixed(4) : 'N/A'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============== SECTION B: CROP INFORMATION ============== */}
      <Card>
        <CardHeader>
          <CardTitle>Section B: Crop Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {crops.map((crop, index) => (
            <div key={crop.crop_id} className="border rounded-lg p-4 space-y-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Crop {index + 1}</h4>
                {crops.length > 1 && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeCrop(crop.crop_id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Linked Parcel */}
                <div className="space-y-2">
                  <Label>Land Parcel <span className="text-red-500">*</span></Label>
                  <Select
                    value={crop.linked_parcel_id}
                    onValueChange={(value) => updateCrop(crop.crop_id, 'linked_parcel_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parcel" />
                    </SelectTrigger>
                    <SelectContent>
                      {parcels.map((p, idx) => (
                        <SelectItem key={p.parcel_id} value={p.parcel_id}>
                          Parcel {idx + 1} {p.survey_number && `(${p.survey_number})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Crop Category */}
                <div className="space-y-2">
                  <Label>Crop Category <span className="text-red-500">*</span></Label>
                  <Select
                    value={crop.category || ''}
                    onValueChange={(value) => handleCropCategoryChange(crop.crop_id, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {cropCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Crop Type */}
                <div className="space-y-2">
                  <Label>Crop Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={crop.crop_type || ''}
                    onValueChange={(value) => handleCropTypeChange(crop.crop_id, value)}
                    disabled={!crop.category}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={crop.category ? "Select crop" : "Select category first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(crop.availableCrops || []).map((c) => (
                        <SelectItem key={c.id} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Year */}
                <div className="space-y-2">
                  <Label>Year <span className="text-red-500">*</span></Label>
                  <Select
                    value={crop.year ? crop.year.toString() : ''}
                    onValueChange={(value) => updateCrop(crop.crop_id, 'year', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Season */}
                <div className="space-y-2">
                  <Label>Season <span className="text-red-500">*</span></Label>
                  <Select
                    value={crop.season || ''}
                    onValueChange={(value) => updateCrop(crop.crop_id, 'season', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select season" />
                    </SelectTrigger>
                    <SelectContent>
                      {seasons.map((s) => (
                        <SelectItem key={s.id} value={s.name}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Crop Area */}
                <div className="space-y-2">
                  <Label>Crop Area (acres) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={crop.area}
                    onChange={(e) => updateCrop(crop.crop_id, 'area', e.target.value)}
                    placeholder="Enter area in acres"
                  />
                </div>
              </div>

              {/* Yield Unit Display */}
              {crop.yield_unit && (
                <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                  <strong>Yield Unit:</strong> {crop.yield_unit}
                </div>
              )}
            </div>
          ))}

          <Button
            type="button"
            onClick={addCrop}
            variant="outline"
            className="w-full"
            disabled={crops.length >= 5}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Crop {crops.length >= 5 && '(Maximum 5 reached)'}
          </Button>

          {/* Crop Summary */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Crop Diversification Index:</span>
              <span>{cdi !== null ? cdi.toFixed(4) : 'N/A'}</span>
            </div>
          </div>

          {/* Validation Warnings */}
          {validateParcelAreas().map((warning, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
              <AlertTriangle className="w-4 h-4" />
              <span>{warning}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ============== SECTION C: HISTORICAL YIELD ============== */}
      <Card>
        <CardHeader>
          <CardTitle>Section C: Historical Yield (3-Year Data)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {crops.filter(c => c.crop_type && c.year && c.season).map((crop, cropIndex) => (
            <div key={crop.crop_id} className="border rounded-lg p-4 space-y-4 bg-gray-50">
              <h4 className="font-semibold">
                {crop.crop_type} ({crop.year} {crop.season})
              </h4>

              <div className="space-y-3">
                {(crop.yield_history || []).map((yieldEntry, yearIndex) => (
                  <div key={yearIndex} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Input value={yieldEntry.year} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Season</Label>
                      <Input value={yieldEntry.season} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Yield ({yieldEntry.yield_unit}) <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={yieldEntry.yield_value || ''}
                        onChange={(e) => updateYieldHistory(crop.crop_id, yearIndex, e.target.value)}
                        placeholder="Enter yield"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Auto-calculated values */}
              {crop.avg_yield !== null && crop.yield_variance !== null && (
                <div className="bg-blue-50 p-3 rounded space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Average Yield:</span>
                    <span>{crop.avg_yield.toFixed(2)} {crop.yield_unit}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Yield Variance (Std Dev):</span>
                    <span>{crop.yield_variance.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {crops.filter(c => c.crop_type && c.year && c.season).length === 0 && (
            <div className="text-center text-gray-500 py-4">
              Complete crop details (Crop Type, Year, Season) to enter yield history
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Step3Agricultural;
